import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Company, type Prospect } from '../lib/api';
import { useDebounced } from '../lib/hooks';
import { cx } from './ui';

/**
 * Static, navigable destinations — kept separate from `Layout.tsx`'s `NAV`
 * (which also carries `adminOnly`/`end` for the top bar) rather than shared,
 * since a palette entry needs nothing beyond a label and a path.
 */
const PAGES = [
  { label: 'Dashboard', to: '/' },
  { label: 'Today', to: '/today' },
  { label: 'Prospects', to: '/prospects' },
  { label: 'Companies', to: '/companies' },
  { label: 'Pipeline', to: '/pipeline' },
  { label: 'Review queue', to: '/queue' },
  { label: 'Replies', to: '/replies' },
  { label: 'Campaigns', to: '/campaigns' },
  { label: 'LinkedIn', to: '/posts' },
  { label: 'Analytics', to: '/analytics' },
  { label: 'Ask', to: '/ask' },
  { label: 'AI Usage', to: '/ai-usage' },
  { label: 'Settings', to: '/settings' },
  { label: 'Guides', to: '/guides' },
];

type Item =
  | { kind: 'page'; key: string; label: string; sublabel?: string; to: string }
  | { kind: 'prospect'; key: string; label: string; sublabel?: string; to: string }
  | { kind: 'company'; key: string; label: string; sublabel?: string; to: string };

const GROUP_LABEL: Record<Item['kind'], string> = {
  page: 'Pages',
  prospect: 'Prospects',
  company: 'Companies',
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const debouncedQuery = useDebounced(query, 200);

  // Cmd+K / Ctrl+K opens from anywhere; Escape closes. The header button
  // (no direct handle on this component's state) opens it via the same
  // custom event rather than lifting state up into Layout.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((wasOpen) => !wasOpen);
      } else if (event.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const onOpenEvent = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('command-palette:open', onOpenEvent);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('command-palette:open', onOpenEvent);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlighted(0);
      setProspects([]);
      setCompanies([]);
      // Focus after the modal has mounted, not synchronously.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setHighlighted(0);
  }, [debouncedQuery]);

  useEffect(() => {
    if (!open || debouncedQuery.trim().length < 2) {
      setProspects([]);
      setCompanies([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    Promise.all([
      api.get<{ prospects: Prospect[] }>(
        `/api/prospects?search=${encodeURIComponent(debouncedQuery)}&limit=6`,
      ),
      api.get<{ companies: Company[] }>(
        `/api/companies?q=${encodeURIComponent(debouncedQuery)}&limit=6`,
      ),
    ])
      .then(([prospectRes, companyRes]) => {
        if (cancelled) return;
        setProspects(prospectRes.prospects);
        setCompanies(companyRes.companies);
      })
      .catch((err: unknown) => {
        if (!cancelled) console.error('[CommandPalette] search failed', err);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery]);

  const items = useMemo<Item[]>(() => {
    const q = query.trim().toLowerCase();
    const pages: Item[] = (q ? PAGES.filter((p) => p.label.toLowerCase().includes(q)) : PAGES).map(
      (p) => ({ kind: 'page', key: `page:${p.to}`, label: p.label, to: p.to }),
    );
    const prospectItems: Item[] = prospects.map((p) => ({
      kind: 'prospect',
      key: `prospect:${p.id}`,
      label: `${p.first_name} ${p.last_name}`.trim() || p.email || 'Unnamed prospect',
      sublabel: [p.job_title, p.company_name].filter(Boolean).join(' · ') || undefined,
      to: `/prospects/${p.id}`,
    }));
    const companyItems: Item[] = companies.map((c) => ({
      kind: 'company',
      key: `company:${c.id}`,
      label: c.name,
      sublabel: c.domain ?? undefined,
      to: `/companies/${c.id}`,
    }));
    return [...pages, ...prospectItems, ...companyItems];
  }, [query, prospects, companies]);

  const groups = useMemo(() => {
    const byKind = new Map<Item['kind'], Item[]>();
    for (const item of items) {
      const existing = byKind.get(item.kind) ?? [];
      existing.push(item);
      byKind.set(item.kind, existing);
    }
    return byKind;
  }, [items]);

  const go = (item: Item) => {
    setOpen(false);
    navigate(item.to);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((i) => Math.min(i + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[highlighted];
      if (item) go(item);
    }
  };

  if (!open) return null;

  let runningIndex = -1;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-ink-950/40 p-4 pt-[12vh]">
      <div className="absolute inset-0" onClick={() => setOpen(false)} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="relative w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-ink-100 px-4 py-3">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 flex-none text-ink-400">
            <path
              fillRule="evenodd"
              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
              clipRule="evenodd"
            />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Jump to a page, prospect or company…"
            className="w-full border-0 bg-transparent text-sm text-ink-900 outline-none placeholder:text-ink-400"
          />
          {searching && <span className="flex-none text-xs text-ink-400">Searching…</span>}
          <kbd className="flex-none rounded border border-ink-200 bg-ink-50 px-1.5 py-0.5 text-[11px] text-ink-500">
            Esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-1.5">
          {items.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-ink-400">
              {query.trim().length >= 2 ? 'No matches.' : 'Type at least 2 characters to search prospects and companies.'}
            </p>
          )}
          {(['page', 'prospect', 'company'] as const).map((kind) => {
            const groupItems = groups.get(kind);
            if (!groupItems || groupItems.length === 0) return null;
            return (
              <div key={kind} className="mb-1 last:mb-0">
                <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                  {GROUP_LABEL[kind]}
                </p>
                {groupItems.map((item) => {
                  runningIndex += 1;
                  const index = runningIndex;
                  return (
                    <button
                      key={item.key}
                      onMouseEnter={() => setHighlighted(index)}
                      onClick={() => go(item)}
                      className={cx(
                        'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm',
                        index === highlighted ? 'bg-accent-50 text-accent-900' : 'text-ink-700 hover:bg-ink-50',
                      )}
                    >
                      <span className="truncate">{item.label}</span>
                      {item.sublabel && (
                        <span className="flex-none truncate text-xs text-ink-400">{item.sublabel}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
